import {Expose, plainToClassFromExist, Type} from 'class-transformer';
import {AbstractApiModel} from './abstract.api.model';
import {MediaObjectApiModel} from './media-object.api-model';
import {EventViewModel} from '../../shared/modal/components/add-event/event.view-model';
import {ParticipantApiModel} from './participant.api.model';
import {PostApiModel} from './post-api.model';
import {FollowApiModel} from './follow.api-model';
import {ReactionApiModel} from './reaction.api.model';
import {MetadataApiModel} from './metadata.api.model';
import {HashtagApiModel} from './hashtag.api.model';
import {EventInvitationApiModel} from './event-invitation.api-model';
import {EventParticipationRequestVehicleApiModel} from './event-participation-request-vehicle.api-model';
import {EventLocationAddress} from '../../shared/event/event-location.util';

export interface EventSummaryApiModel {
  id: number;
  name: string;
  slug: string;
}

export class EventApiModel extends AbstractApiModel {
  @Expose() public id: number;
  @Expose() public cover: MediaObjectApiModel|string;
  @Expose() public name: string;
  @Expose() public slug: string;
  @Expose() public description: string;
  @Expose() public eventTypes: {name: string, slug: string}[];
  @Expose() public faq: {question: string, answer: string}[];
  @Expose() public similarEvents: EventSummaryApiModel[];
  @Expose() public visibility: string;
  @Expose() public eventDates: {name: string, from: Date, to: Date, duration?: number, start: Date, end: Date}[];
  @Expose() public addresses: EventLocationAddress[];
  @Expose() public organizers: ParticipantApiModel[]|string[];
  @Expose() public participants: ParticipantApiModel[];
  @Expose() public follows: FollowApiModel[];
  @Expose()
  @Type(() => HashtagApiModel)
  public hashtags: (HashtagApiModel|string)[];
  // @Expose({toPlainOnly: true, name: 'hashtags'})
  // public strHashtags: string[];
  @Expose() public reactions: ReactionApiModel[];
  @Expose() public metadata: MetadataApiModel;
  @Expose()
  @Type(() => EventInvitationApiModel)
  public invitations: EventInvitationApiModel[];
  @Expose() public invitedUsers: string[];
  @Expose() public invitationId?: number;
  @Expose() public invitationStatus?: string;
  @Expose() public attendanceDeclarationId?: number;
  @Expose() public attendanceDeclarationStatus?: 'ACTIVE' | 'CANCELLED';
  @Expose() public participationRequestId?: number;
  @Expose() public participationRequestStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  @Expose() public participationRequestRejectionReason?: string | null;
  @Expose()
  @Type(() => EventParticipationRequestVehicleApiModel)
  public participationRequestVehicles?: EventParticipationRequestVehicleApiModel[];
  @Expose()
  @Type(() => PostApiModel)
  public posts: (PostApiModel | string)[];
  @Expose()
  @Type(() => MediaObjectApiModel)
  public media: (MediaObjectApiModel | string)[];
  public identity: string;
  @Expose() public updatedAt: Date;
  @Expose() public createdAt: Date;
  public isPlaceholder = false;

  public static fromEventVM(eventVM: EventViewModel): EventApiModel {
    const eventAM = plainToClassFromExist(eventVM.apiModel, eventVM);
    eventAM.cover = eventVM.cover ? eventVM.cover['@id'] : null;
    eventAM.organizers = eventVM.organizers.map(e => e.user['@id']);
    eventAM.visibility = eventVM.visibility;
    eventAM.invitedUsers = eventVM.invitedUsers?.map(e => e.user['@id']) ?? [];
    eventAM.media = eventVM.media.map(e => e['@id'] || e);
    eventAM.posts = eventVM.posts.map(e => e['@id'] || e);
    eventAM.hashtags = eventVM.hashtags.map(e => e['name'] || e);
    eventAM.addresses = [eventVM.addresses[0].value];
    eventAM.metadata = new MetadataApiModel();
    eventAM.metadata.metaTitle  = eventVM.metaTitle;
    eventAM.metadata.metaDescription  = eventVM.metaDescription;
    eventAM.metadata.metaKeywords  = eventVM.metaKeywords;
    eventAM.eventDates = eventVM.eventDates.map((dateObj: {name: string, date: Date, from: Date, to: Date }) => {
      const date = dateObj.date;
      const from = new Date(date.valueOf());
      const to = new Date(date.valueOf());
      from.setHours(dateObj.from.getHours(), dateObj.from.getMinutes());
      to.setHours(dateObj.to.getHours(), dateObj.to.getMinutes());
      return {
        name: dateObj.name,
        from,
        to,
        start: from,
        end: to
      };
    });

    return eventAM;
  }
}
